import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import type { CentralRiskEventBbox } from '../central-monitoring/centralRiskEventTypes';
import { formatBboxAlertDetailLines } from '../central-monitoring/centralRiskEventTypes';
import { eventService } from '../../services/eventService';
import {
  composeEventSnapshotWithBboxes,
  dataUrlToBase64,
} from '../../utils/composeEventSnapshot';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';
import { getBearerTokenForApi } from '../../utils/poolSiteAuth';
import EditableSnapshotBboxEditor from './EditableSnapshotBboxEditor';

type Props = {
  eventId: number | string;
  /** 목록/상세에 보이는 이벤트 메시지(초기값) */
  initialMessage?: string;
  onSaved?: (info: {
    imageUrl: string;
    message?: string;
    riskLabel?: string;
  }) => void;
};

async function fetchImageAsDataUrl(url: string): Promise<string | null> {
  const token = getBearerTokenForApi();
  const headers: HeadersInit = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { headers, credentials: 'include' });
  if (!res.ok) return null;
  const blob = await res.blob();
  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

function messageHeader(current: string): string {
  const first = (current || '').split('\n')[0]?.trim() || '';
  if (!first || /^\d+\)/.test(first)) {
    return '이벤트 발생. 즉시 현장 확인';
  }
  return first;
}

function rebuildMessageFromBboxes(
  current: string,
  boxes: CentralRiskEventBbox[]
): string {
  const detail = formatBboxAlertDetailLines(boxes);
  const header = messageHeader(current);
  return detail ? `${header}\n${detail}` : header;
}

/**
 * 이벤트 목록/상세에서 저장된 캡처 bbox를 불러와 편집·저장.
 * 저장 시 snapshot.jpg 가 다시 베이킹되어 썸네일도 동일하게 반영된다.
 */
const EventSnapshotBboxEditPanel: React.FC<Props> = ({
  eventId,
  initialMessage = '',
  onSaved,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [rawDataUrl, setRawDataUrl] = useState<string | null>(null);
  const [bboxes, setBboxes] = useState<CentralRiskEventBbox[]>([]);
  const [hasRaw, setHasRaw] = useState(false);
  const [scenarioProfile, setScenarioProfile] = useState<string | null>('pool');
  const [detectionScenario, setDetectionScenario] = useState<string | null>(null);
  const [eventRiskLabel, setEventRiskLabel] = useState<string>('');
  const [selectedBboxIndex, setSelectedBboxIndex] = useState<number | null>(null);
  const [editRiskLabel, setEditRiskLabel] = useState('');
  const [editRiskDescription, setEditRiskDescription] = useState('');
  const [editBboxColor, setEditBboxColor] = useState('#ff1744');
  const [editMessage, setEditMessage] = useState(initialMessage || '');

  const applyTargetIndex = useMemo(() => {
    if (
      selectedBboxIndex != null &&
      selectedBboxIndex >= 0 &&
      selectedBboxIndex < bboxes.length
    ) {
      return selectedBboxIndex;
    }
    if (bboxes.length > 0) return bboxes.length - 1;
    return null;
  }, [selectedBboxIndex, bboxes.length]);

  /** 입력란에 있는 코드명·설명을 대상 bbox에 반영 (저장 직전에도 사용) */
  const mergePendingOntoBboxes = useCallback(
    (list: CentralRiskEventBbox[]): CentralRiskEventBbox[] => {
      const idx =
        selectedBboxIndex != null &&
        selectedBboxIndex >= 0 &&
        selectedBboxIndex < list.length
          ? selectedBboxIndex
          : list.length > 0
            ? list.length - 1
            : null;
      const label = editRiskLabel.trim();
      if (idx == null || !label) return list;
      const desc = editRiskDescription.trim();
      return list.map((b, i) =>
        i === idx
          ? {
              ...b,
              motionRisk: true,
              riskSubtypeLabel: label,
              riskDescription: desc,
              borderColor: editBboxColor,
            }
          : b
      );
    },
    [selectedBboxIndex, editRiskLabel, editRiskDescription, editBboxColor]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNote(null);
    try {
      const meta = await eventService.getRiskSnapshotMeta(eventId);
      setHasRaw(Boolean(meta.has_raw));
      setScenarioProfile(meta.scenario_profile || 'pool');
      setDetectionScenario(meta.detection_scenario || null);
      const savedLabel =
        (meta.risk_label && String(meta.risk_label).trim()) ||
        (Array.isArray(meta.risk_labels) && meta.risk_labels[0]
          ? String(meta.risk_labels[0]).trim()
          : '');
      setEventRiskLabel(savedLabel);

      const boxes = Array.isArray(meta.bboxes)
        ? (meta.bboxes as CentralRiskEventBbox[]).map((b) => ({
            ...b,
            bbox: [...(b.bbox || [])],
          }))
        : [];
      setBboxes(boxes);

      const msgFromMeta =
        (meta.message && String(meta.message).trim()) ||
        String(initialMessage || '').trim();
      setEditMessage(
        msgFromMeta || rebuildMessageFromBboxes('', boxes)
      );

      const firstLabeled = boxes.find(
        (b) =>
          (b.riskSubtypeLabel || '').trim() || (b.riskDescription || '').trim()
      );
      if (firstLabeled) {
        setEditRiskLabel(firstLabeled.riskSubtypeLabel || '');
        setEditRiskDescription(String(firstLabeled.riskDescription || ''));
        if (firstLabeled.borderColor) setEditBboxColor(firstLabeled.borderColor);
        setSelectedBboxIndex(boxes.indexOf(firstLabeled));
      } else if (savedLabel) {
        setEditRiskLabel(savedLabel);
        setEditRiskDescription('');
      } else {
        setEditRiskLabel('');
        setEditRiskDescription('');
        setSelectedBboxIndex(boxes.length > 0 ? boxes.length - 1 : null);
      }

      const rawUrl = `${meta.raw_image_url}?t=${Date.now()}`;
      const dataUrl = await fetchImageAsDataUrl(rawUrl);
      if (!dataUrl) {
        setError('캡처 이미지를 불러오기 실패');
        setRawDataUrl(null);
        return;
      }
      setRawDataUrl(dataUrl);
      if (!meta.has_raw) {
        setNote(
          '원본(raw) 캡처가 없어 현재 저장된 이미지로 편집. 이중 표시를 피하려면 bbox를 정리한 뒤 저장.'
        );
      }
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '캡처 정보를 불러오기 실패'));
      setRawDataUrl(null);
    } finally {
      setLoading(false);
    }
  }, [eventId, initialMessage]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyCodeNameAndColor = () => {
    const idx = applyTargetIndex;
    if (idx == null) {
      setNote('먼저 bbox 선택 또는 작성');
      return;
    }
    const label = editRiskLabel.trim();
    if (!label) {
      setNote('코드명을 입력');
      return;
    }
    const next = mergePendingOntoBboxes(bboxes);
    setBboxes(next);
    setSelectedBboxIndex(idx);
    setEventRiskLabel(label);
    setEditMessage((prev) => rebuildMessageFromBboxes(prev, next));
    setNote(`#${idx + 1} bbox에 코드명·설명·색 적용 (메시지에도 반영)`);
  };

  const handleSave = async () => {
    if (!rawDataUrl) return;
    setSaving(true);
    setError(null);
    setNote(null);
    try {
      // 「코드명 적용」을 누르지 않아도 저장 시 입력란 값을 bbox에 반영
      const boxesToSave = mergePendingOntoBboxes(bboxes);
      let messageToSave = editMessage.trim();
      const detail = formatBboxAlertDetailLines(boxesToSave);
      if (detail) {
        const missingCode = boxesToSave.some((b) => {
          const c = (b.riskSubtypeLabel || '').trim();
          return Boolean(c) && !messageToSave.includes(c);
        });
        if (!messageToSave || missingCode) {
          messageToSave = rebuildMessageFromBboxes(messageToSave, boxesToSave);
        }
      } else if (!messageToSave) {
        messageToSave = messageHeader('');
      }
      setBboxes(boxesToSave);
      setEditMessage(messageToSave);

      const composed =
        boxesToSave.length > 0
          ? await composeEventSnapshotWithBboxes(rawDataUrl, boxesToSave)
          : rawDataUrl;
      const baked = dataUrlToBase64(composed);
      const raw = dataUrlToBase64(rawDataUrl);
      if (!baked) {
        setError('이미지 인코딩에 실패');
        return;
      }
      const res = await eventService.updateRiskSnapshot({
        eventId,
        snapshotBase64: baked,
        snapshotRawBase64: hasRaw ? raw : raw,
        bboxes: boxesToSave,
        message: messageToSave,
      });
      const riskLabel =
        boxesToSave.find((b) => (b.riskSubtypeLabel || '').trim())?.riskSubtypeLabel ||
        eventRiskLabel ||
        undefined;
      if (riskLabel) setEventRiskLabel(riskLabel);
      setNote('저장 완료. 캡처·메시지·코드명이 반영됨');
      onSaved?.({
        imageUrl: res.image_url
          ? `${res.image_url}?t=${Date.now()}`
          : `/api/events/${eventId}/risk-snapshot?t=${Date.now()}`,
        message: res.message ?? messageToSave,
        riskLabel: res.risk_label || riskLabel,
      });
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '캡처 저장에 실패'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={22} />
        <Typography variant="body2" color="text.secondary">
          캡처 불러오는 중…
        </Typography>
      </Box>
    );
  }

  if (error && !rawDataUrl) {
    return (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  }

  if (!rawDataUrl) return null;

  const bboxLabels = bboxes
    .map((b) => (b.riskSubtypeLabel || '').trim())
    .filter(Boolean);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        발생 시점 캡처 (bbox 편집)
      </Typography>
      {(eventRiskLabel || bboxLabels.length > 0) && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          발생 시 위험코드명:{' '}
          {eventRiskLabel || bboxLabels.join(', ')}
          {applyTargetIndex != null ? ` · 선택 #${applyTargetIndex + 1}` : ''}
        </Typography>
      )}
      <EditableSnapshotBboxEditor
        imageUrl={rawDataUrl}
        bboxes={bboxes}
        onChange={(next) => {
          setBboxes(next);
          if (next.length > 0 && selectedBboxIndex == null) {
            setSelectedBboxIndex(next.length - 1);
          }
        }}
        selectedIndex={selectedBboxIndex}
        onSelectedIndexChange={(idx) => {
          setSelectedBboxIndex(idx);
          if (idx != null && bboxes[idx]) {
            const b = bboxes[idx];
            setEditRiskLabel(String(b.riskSubtypeLabel || ''));
            setEditRiskDescription(String(b.riskDescription || ''));
            if (b.borderColor) setEditBboxColor(b.borderColor);
          }
        }}
        scenarioProfile={scenarioProfile}
        detectionScenario={detectionScenario}
        maxHeight={420}
        onEventTypeApplied={(info) => {
          setEditRiskLabel(info.label);
          setEditBboxColor(info.color);
          setEventRiskLabel(info.label);
          setNote(`이벤트 종류「${info.label}」을(를) 선택 박스에 적용함`);
          // onChange로 bbox는 이미 갱신됨 — 다음 틱에 메시지 동기화
          window.setTimeout(() => {
            setBboxes((cur) => {
              setEditMessage((prev) => rebuildMessageFromBboxes(prev, cur));
              return cur;
            });
          }, 0);
        }}
      />
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            label="코드명"
            placeholder="발생 시 입력한 위험코드명"
            value={editRiskLabel}
            onChange={(e) => setEditRiskLabel(e.target.value)}
            sx={{ width: 240 }}
          />
          <input
            type="color"
            value={/^#[0-9a-fA-F]{6}$/.test(editBboxColor) ? editBboxColor : '#ff1744'}
            onChange={(e) => setEditBboxColor(e.target.value)}
            style={{ width: 36, height: 36, border: 'none', padding: 0, cursor: 'pointer' }}
            aria-label="bbox 색"
            title="bbox 색"
          />
          <Button
            size="small"
            variant="outlined"
            disabled={bboxes.length === 0}
            onClick={applyCodeNameAndColor}
          >
            코드명 적용
          </Button>
          <Button variant="contained" size="small" onClick={() => void handleSave()} disabled={saving}>
            {saving ? '저장 중…' : '캡처·메시지 저장'}
          </Button>
          <Button size="small" onClick={() => void load()} disabled={saving}>
            다시 불러오기
          </Button>
        </Box>
        <TextField
          size="small"
          label="설명"
          placeholder="현장 전달용 설명"
          value={editRiskDescription}
          onChange={(e) => setEditRiskDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
        <TextField
          size="small"
          label="이벤트 메시지"
          placeholder="알림·목록에 표시되는 메시지"
          value={editMessage}
          onChange={(e) => setEditMessage(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          helperText="코드명 적용·저장 시 bbox 코드명·설명이 아래에 반영됨. 직접 수정도 가능."
        />
        {note ? (
          <Typography variant="caption" color="text.secondary">
            {note}
          </Typography>
        ) : null}
        {error ? (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default EventSnapshotBboxEditPanel;
